// frontend/src/components/surveys/SurveyAnalytics.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Users, Star, TrendingUp, MessageSquare, Calendar, Download,
    Car, Monitor, ChevronLeft, ChevronRight, BarChart2
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedCard, ThemedButton, ThemedBadge } from '../themed';
import surveyService from '../../services/surveyService';
import { formatDate } from '../../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';

/**
 * Survey Analytics Component
 * Displays survey response statistics and analytics
 */
const SurveyAnalytics = ({ entityId, canViewService, canViewSystem }) => {
    const { t } = useTranslation();
    const { text, cn } = useTheme();
    const { dateFormat } = useDateTimePreferences();
    
    const [loading, setLoading] = useState(true);
    const [activeType, setActiveType] = useState(canViewService ? 'service' : 'system');
    const [dateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
        endDate: new Date()
    });
    const [analytics, setAnalytics] = useState(null);
    const [textResponses, setTextResponses] = useState({});
    const [currentPage, setCurrentPage] = useState({});

    /**
     * Load analytics data
     */
    const loadAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            const data = await surveyService.getSurveyAnalytics(entityId, activeType, {
                startDate: dateRange.startDate.toISOString(),
                endDate: dateRange.endDate.toISOString()
            });
            setAnalytics(data);
        } catch (error) {
            console.error('Error loading analytics:', error);
        } finally {
            setLoading(false);
        }
    }, [entityId, activeType, dateRange]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    /**
     * Load text responses for a specific question
     */
    const loadTextResponses = async (questionId, page = 1) => {
        try {
            const data = await surveyService.getTextResponses(entityId, questionId, page, 10);
            setTextResponses(prev => ({
                ...prev,
                [questionId]: data
            }));
            setCurrentPage(prev => ({
                ...prev,
                [questionId]: page
            }));
        } catch (error) {
            console.error('Error loading text responses:', error);
        }
    };

    /**
     * Export survey data
     */
    const handleExport = async (format) => {
        const success = await surveyService.exportSurveyData(entityId, activeType, format, {
            startDate: dateRange.startDate.toISOString(),
            endDate: dateRange.endDate.toISOString()
        });
        
        if (success) {
            toast.success(t('dataExportedSuccessfully'));
        } else {
            toast.error(t('exportFailed'));
        }
    };

    /**
     * Render star rating visualization
     */
    const renderStarRating = (rating) => {
        return (
            <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        className={cn(
                            "w-4 h-4",
                            star <= Math.round(rating)
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-gray-300 dark:text-gray-600"
                        )}
                    />
                ))}
                <span className={cn("ml-2 text-sm font-medium", text.primary)}>
                    {rating.toFixed(1)}
                </span>
            </div>
        );
    };

    /**
     * Render distribution chart for scale questions
     */
    const renderDistributionChart = (distribution) => {
        const maxCount = Math.max(...Object.values(distribution || {}), 1);
        
        return (
            <div className="space-y-2">
	                {[5, 4, 3, 2, 1].map((rating) => {
	                    const count = distribution?.[rating] || 0;
	                    const percentage = (count / maxCount) * 100;
	                    const ratingLabel = `${rating}/5: ${count} ${t('responses')}`;

	                    return (
	                        <div key={rating} className="flex items-center gap-3">
                            <div className="flex items-center gap-1 w-20">
                                <span className={cn("text-sm", text.secondary)}>
                                    {rating}
                                </span>
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            </div>
	                            <div className="flex-1">
	                                <div
	                                    className={cn("h-6 rounded-full overflow-hidden", "bg-gray-200 dark:bg-gray-700")}
	                                    role="progressbar"
	                                    aria-label={ratingLabel}
	                                    aria-valuemin={0}
	                                    aria-valuemax={maxCount}
	                                    aria-valuenow={count}
	                                    aria-valuetext={ratingLabel}
	                                >
	                                    <div
	                                        className="h-full bg-blue-500 transition-all duration-500"
	                                        style={{ width: `${percentage}%` }}
	                                        aria-hidden="true"
	                                    />
	                                </div>
	                            </div>
                            <span className={cn("text-sm w-12 text-right", text.secondary)}>
                                {count}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loadingAnalytics')}...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Controls */}
            <ThemedCard className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    {/* Survey Type Selector */}
                    <div className="flex items-center gap-2">
                        {canViewService && (
                            <ThemedButton
                                variant={activeType === 'service' ? 'primary' : 'secondary'}
                                size="sm"
                                onClick={() => setActiveType('service')}
                            >
                                <Car className="w-4 h-4 mr-2" />
                                {t('serviceSurvey')}
                            </ThemedButton>
                        )}
                        {canViewSystem && (
                            <ThemedButton
                                variant={activeType === 'system' ? 'primary' : 'secondary'}
                                size="sm"
                                onClick={() => setActiveType('system')}
                            >
                                <Monitor className="w-4 h-4 mr-2" />
                                {t('systemSurvey')}
                            </ThemedButton>
                        )}
                    </div>

                    {/* Date Range Selector */}
                    <div className="flex items-center gap-2">
                        <Calendar className={cn("w-4 h-4", text.secondary)} />
                        <span className={cn("text-sm", text.secondary)}>
                            {formatDate(dateRange.startDate, dateFormat)} - {formatDate(dateRange.endDate, dateFormat)}
                        </span>
                    </div>

                    {/* Export Buttons */}
                    <div className="flex items-center gap-2">
                        <ThemedButton
                            variant="secondary"
                            size="sm"
                            onClick={() => handleExport('csv')}
                        >
                            <Download className="w-4 h-4 mr-2" />
                            CSV
                        </ThemedButton>
                        <ThemedButton
                            variant="secondary"
                            size="sm"
                            onClick={() => handleExport('xlsx')}
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Excel
                        </ThemedButton>
                    </div>
                </div>
            </ThemedCard>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title={t('totalResponses')}
                    value={analytics?.summary?.totalResponses || 0}
                    icon={<Users className="w-5 h-5" />}
                    trend={analytics?.summary?.responseTrend}
                />
                <StatCard
                    title={t('averageScore')}
                    value={`${(analytics?.summary?.averageScore || 0).toFixed(1)}/5`}
                    icon={<Star className="w-5 h-5" />}
                    trend={analytics?.summary?.scoreTrend}
                />
                <StatCard
                    title={t('responseRate')}
                    value={`${(analytics?.summary?.responseRate || 0).toFixed(0)}%`}
                    icon={<TrendingUp className="w-5 h-5" />}
                    trend={analytics?.summary?.rateTrend}
                />
                <StatCard
                    title={t('uniqueRespondents')}
                    value={analytics?.summary?.uniqueRespondents || 0}
                    icon={<Users className="w-5 h-5" />}
                />
            </div>

            {/* Question-Level Analytics */}
            <div className="space-y-4">
                {analytics?.questions?.map((question) => (
                    <ThemedCard key={question.id} className="p-6">
                        <div className="space-y-4">
                            {/* Question Header */}
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <h3 className={cn("font-medium text-lg", text.primary)}>
                                        {question.question_text}
                                    </h3>
                                    <div className="flex items-center gap-4 mt-2">
                                        <ThemedBadge variant={question.question_type === 'scale' ? 'primary' : 'secondary'}>
                                            {question.question_type === 'scale' ? (
                                                <>
                                                    <Star className="w-3 h-3 mr-1" />
                                                    {t('ratingQuestion')}
                                                </>
                                            ) : (
                                                <>
                                                    <MessageSquare className="w-3 h-3 mr-1" />
                                                    {t('textQuestion')}
                                                </>
                                            )}
                                        </ThemedBadge>
                                        <span className={cn("text-sm", text.secondary)}>
                                            {question.response_count} {t('responses')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Question Analytics Content */}
                            {question.question_type === 'scale' ? (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Average Rating */}
                                    <div>
                                        <h4 className={cn("text-sm font-medium mb-3", text.secondary)}>
                                            {t('averageRating')}
                                        </h4>
                                        {renderStarRating(question.avg_score || 0)}
                                        <div className="mt-2 flex items-center gap-4 text-sm">
                                            <span className={cn(text.secondary)}>
                                                {t('min')}: {question.min_score || 0}
                                            </span>
                                            <span className={cn(text.secondary)}>
                                                {t('max')}: {question.max_score || 0}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Distribution Chart */}
                                    <div>
                                        <h4 className={cn("text-sm font-medium mb-3", text.secondary)}>
                                            {t('ratingDistribution')}
                                        </h4>
                                        {renderDistributionChart(question.distribution)}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <h4 className={cn("text-sm font-medium mb-3", text.secondary)}>
                                        {t('sampleResponses')}
                                    </h4>
                                    <TextResponseList
                                        questionId={question.id}
                                        responses={textResponses[question.id]}
                                        currentPage={currentPage[question.id] || 1}
                                        onLoadResponses={loadTextResponses}
                                    />
                                </div>
                            )}
                        </div>
                    </ThemedCard>
                ))}
            </div>

            {/* No Data State */}
            {(!analytics?.questions || analytics.questions.length === 0) && (
                <ThemedCard className="p-8">
                    <div className={cn("text-center", text.secondary)}>
                        <BarChart2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <h3 className={cn("text-lg font-medium mb-2", text.primary)}>
                            {t('noAnalyticsData')}
                        </h3>
                        <p>{t('noResponsesReceivedYet')}</p>
                    </div>
                </ThemedCard>
            )}
        </div>
    );
};

/**
 * Stat Card Component
 */
const StatCard = ({ title, value, icon, trend }) => {
    const { text, cn } = useTheme();
    
    return (
        <ThemedCard className="p-4">
            <div className="flex items-start justify-between">
                <div>
                    <p className={cn("text-sm font-medium", text.secondary)}>
                        {title}
                    </p>
                    <p className={cn("text-2xl font-bold mt-1", text.primary)}>
                        {value}
                    </p>
                    {trend !== undefined && (
                        <p className={cn(
                            "text-sm mt-1",
                            trend > 0 ? "text-green-600" : trend < 0 ? "text-red-600" : text.secondary
                        )}>
                            {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend)}%
                        </p>
                    )}
                </div>
                <div className={cn("p-2 rounded-lg", "bg-blue-50 dark:bg-blue-900/20")}>
                    {React.cloneElement(icon, { className: "text-blue-600 dark:text-blue-400" })}
                </div>
            </div>
        </ThemedCard>
    );
};

/**
 * Text Response List Component
 */
const TextResponseList = ({ questionId, responses, currentPage, onLoadResponses }) => {
    const { t } = useTranslation();
    const { text, border, cn } = useTheme();
    const { dateTimeFormat } = useDateTimePreferences();
    
    useEffect(() => {
        if (!responses) {
            onLoadResponses(questionId, 1);
        }
    }, [onLoadResponses, questionId, responses]);

    if (!responses) {
        return (
            <div className={cn("text-center py-4", text.secondary)}>
                {t('loadingResponses')}...
            </div>
        );
    }

    const totalPages = Math.ceil(responses.total / 10);

    return (
        <div className="space-y-3">
            {responses.responses?.map((response, index) => (
                <div
                    key={response.id || index}
                    className={cn("p-3 rounded-lg border", border.default)}
                >
                    <p className={cn("text-sm", text.primary)}>
                        &quot;{response.text_value}&quot;
                    </p>
                    <p className={cn("text-xs mt-2", text.secondary)}>
                        {formatDate(new Date(response.response_date), dateTimeFormat)} • 
                        {response.anonymous ? t('anonymous') : `User #${response.user_id}`}
                    </p>
                </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-3">
                    <button
                        onClick={() => onLoadResponses(questionId, currentPage - 1)}
                        disabled={currentPage === 1}
                        className={cn(
                            "p-1 rounded",
                            currentPage === 1
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-gray-100 dark:hover:bg-gray-800"
                        )}
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className={cn("text-sm", text.secondary)}>
                        {currentPage} / {totalPages}
                    </span>
                    <button
                        onClick={() => onLoadResponses(questionId, currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={cn(
                            "p-1 rounded",
                            currentPage === totalPages
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-gray-100 dark:hover:bg-gray-800"
                        )}
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {responses.responses?.length === 0 && (
                <p className={cn("text-center py-4", text.secondary)}>
                    {t('noTextResponsesYet')}
                </p>
            )}
        </div>
    );
};

export default SurveyAnalytics;
